import {
  DataUiBlueprints,
  DataUiColumnsDescription,
  JobStatusEnum,
  SimplifiedColumnTypeEnum,
  DataUiColumnDescription,
  SimplifiedRelationType,
  PartnerExperienceEnum,
} from '@gid/models';
import { sentenceCase } from 'change-case';
import {
  makePath,
  getEntityNameFormatter,
} from '../components/crud-list/crud-list-util';
import {
  FieldsDescription,
  ComponentDescription,
} from '../components/crud-list/CrudEditForm.vue';
import _ from 'lodash';
import axios from 'axios';

export function isDKVMissingData(job_view) {
  return (
    job_view.is_installation &&
    ((/^DKV.*\)$/.test(job_view.opportunity.name) &&
      !job_view.job.invoicing_eid) ||
      (job_view.opportunity.sfid == '0061p00000oeC4QAAU' &&
        !job_view.job.eid)) &&
    ([
      JobStatusEnum.PROPOSAL_APPROVAL_PENDING,
      JobStatusEnum.COMPLETED,
      JobStatusEnum.OPEN,
      JobStatusEnum.ACCEPTED,
      JobStatusEnum.FINISHED,
      JobStatusEnum.APPOINTMENT_CONFIRMED,
      JobStatusEnum.STARTED,
    ].includes(job_view.job.status) ||
      (JobStatusEnum.ON_HOLD == job_view.job.status && job_view.job.partner))
  );
}
/* helper to remap model Filters, to filters with values */
const mapFiltersWithModelValues = (modelFilters, model) => {
  return modelFilters.map((filter) => {
    return {
      ...filter,
      value:
        typeof filter.value === 'function' ? filter.value(model) : filter.value,
    };
  });
};

export function excludePrepaymentServices(item) {
  return !['900.Prepayment', '901.DeductionOfPrepayment'].includes(
    item.service_type,
  );
}

export function getBase64(file, formatted = true) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        if (formatted) {
          const encoded = formatBase64(reader.result.toString());
          resolve(encoded);
        } else {
          resolve(reader.result);
        }
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

export function formatBase64(base64) {
  let encoded = base64.replace(/^data:(.*,)?/, '');
  if (encoded.length % 4 > 0) {
    encoded += '='.repeat(4 - (encoded.length % 4));
  }
  return encoded;
}

export function columnToComponent(
  column: DataUiColumnDescription,
  callerComponent: any,
  isBulkEdit?: boolean,
): ComponentDescription {
  const component = _columnToComponent(column);
  if (!component.properties) {
    component.properties = {};
  }

  if (!isBulkEdit) {
    component.properties.valuePath = makePath(
      callerComponent.valuePath,
      column.propertyPath,
    );
  }
  return component;
}

export function _columnToComponent(
  column: DataUiColumnDescription,
): ComponentDescription {
  switch (column?.columnType?.type) {
    case SimplifiedColumnTypeEnum.PRECISION: {
      const { precision, scale } = column.columnType;
      const numDecimalDigists = precision! - scale!;
      const absMax = isNaN(numDecimalDigists)
        ? Number.MAX_SAFE_INTEGER
        : numDecimalDigists * 10 || 1;
      return {
        is: 'CrudNumberInput',
        properties: {
          type: 'number',
          min: -absMax,
          max: absMax,
          step: scale ? 1 / 10 ** scale : 1,
        },
        modelConstructor: Number,
      };
    }
    case SimplifiedColumnTypeEnum.INTEGER:
    case SimplifiedColumnTypeEnum.FLOAT:
      return {
        is: 'CrudNumberInput',
        properties: { type: 'number' },
        modelConstructor: Number,
      };
    case SimplifiedColumnTypeEnum.ENUM:
      return {
        is: 'CrudEnumPicker',
        properties: {
          enumValues: column.enumValues,
          multiple: column.isArray,
          enumValuesWithKeys: column.enumValuesWithKeys,
          $isDisabled: false,
        },
        modelConstructor: String,
      };
    case SimplifiedColumnTypeEnum.BOOLEAN:
      return { is: 'CrudCheckBox', modelConstructor: Boolean };
    case SimplifiedColumnTypeEnum.JSON:
      return { is: 'CrudJsonEditor', modelConstructor: Object };
    case SimplifiedColumnTypeEnum.UUID:
    case SimplifiedColumnTypeEnum.TEXT:
      if (column.s3Pattern) {
        return {
          is: 'CrudFileInput',
          modelConstructor: Object,
          properties: {
            multiple: column.isArray,
            fullUrl: column.s3Pattern?.fullUrl,
            appendOnly: column.s3Pattern?.arrayMode === 'append',
          },
        };
      }
      if (column.isRichText) {
        return {
          is: 'CrudRichTextEditRichTextEditor',
          modelConstructor: String,
          properties: {},
        };
      }
      if (column.mimeType?.startsWith('text/html')) {
        return {
          is: 'CrudHtmlEditor',
          modelConstructor: String,
          properties: {},
        };
      }
      if (column.isURI) {
        return {
          is: 'CrudLink',
          modelConstructor: String,
          properties: {},
        };
      }
      if (column.enumValuesFromPicklist) {
        return {
          is: 'CrudPicklistMultiSelect',
          modelConstructor: String,
          properties: {
            endpoint: '/data-api/sf-picklist',
            picklistName: column.enumValuesFromPicklist.picklistName,
            multiple: column.enumValuesFromPicklist.multiple,
            flattenDelimiter:
              !column.isArray && column.enumValuesFromPicklist.multiple
                ? ';'
                : undefined,
          },
        };
      }
      if (column.enumValues?.length) {
        return {
          is: 'CrudEnumPicker',
          properties: {
            enumValues: column.enumValues,
            multiple: column.isArray,
            enumValuesWithKeys: column.enumValuesWithKeys,
            $isDisabled: column['$isDisabled'],
          },
          modelConstructor: String,
        };
      }
      return {
        is:
          (column.columnType.length as any) > 100
            ? 'b-form-textarea'
            : 'b-form-input',
        modelConstructor: String,
      };
    case SimplifiedColumnTypeEnum.DATETIME:
      return { is: 'b-form-datepicker', modelConstructor: Date };
    default:
      break;
  }
  return {
    is: null,
    modelConstructor: Object,
  };
}

export function wrapComponent(
  component: ComponentDescription,
  wrapper: string,
) {
  component.properties = {
    properties: component.properties,
    component: component.is,
    modelConstructor: component.modelConstructor,
    valuePath: component.properties.valuePath,
    disabled: component.properties.disabled,
    readonly: component.properties.readonly,
  };
  component.is = wrapper;
}

export function getFields({
  isBulkEdit,
  callerComponent,
  model,
}: {
  isBulkEdit?: boolean;
  callerComponent: any;
  model?: any;
}): FieldsDescription[] {
  const blueprint = isBulkEdit
    ? DataUiBlueprints.get(callerComponent.modelConstructor)?.groups.table
    : DataUiBlueprints.get(callerComponent.store.entityConstructorName)?.groups
        .form;
  if (!blueprint) {
    throw new Error(
      'Failed to find DataUi blueprint for ' + callerComponent.modelConstructor,
    );
  }
  const { default: entities } = require(`@gid/vue-common/entities`);
  const clientModel = entities[callerComponent.modelConstructor.name]?.default;
  // here we extend orm models/columns with client side defined properties
  const mergedObject = _.merge(
    {},
    blueprint,
    clientModel,
  ) as DataUiColumnsDescription;
  const result = Object.entries(mergedObject)
    .filter(([_, config]) => {
      if (isBulkEdit) {
        return (
          !config.isRelation &&
          !config.isPrimary &&
          !config.isReadOnly &&
          !config.s3Pattern
        );
      } else {
        return (
          !config.hide &&
          (config.isRelation
            ? !callerComponent.parents.includes(
                config.relationEntity as Function,
              )
            : true) &&
          (config.isGenerated ||
          config.isCreateDate ||
          config.isUpdateDate ||
          config.isDeleteDate
            ? callerComponent.parents.length == 0
            : true)
        );
      }
    })
    .map(([columnName, config]) => {
      const label = config.label ?? sentenceCase(columnName);
      const name = columnName;
      const result: Partial<FieldsDescription> = {
        name,
        label,
        description: config.description,
        nullable: config.isNullable,
      };

      let filters = null;
      if (config?.filter) {
        filters = mapFiltersWithModelValues(config?.filter, model);
      }

      if (config.isRelation && !isBulkEdit) {
        let multiple = false;
        switch (config.relationType) {
          case SimplifiedRelationType.MANY_TO_MANY:
          case SimplifiedRelationType.ONE_TO_MANY:
            multiple = true;
          // eslint-disable-next-line no-fallthrough
          case SimplifiedRelationType.MANY_TO_ONE:
          case SimplifiedRelationType.ONE_TO_ONE: {
            const relationBlueprint = DataUiBlueprints.get(
              config.relationEntity,
            );
            if (config.isCascadeRelation || config.showInline) {
              const parents = [
                ...callerComponent.parents,
                callerComponent.modelConstructor,
              ];
              result.component = {
                is: multiple ? 'CrudEditArrayForm' : 'CrudEditForm',
                modelConstructor: config.relationEntity as any,
                embeddedForm: true,
                properties: {
                  store: callerComponent.getStore(
                    config.relationEntity as Function,
                  ),
                  valuePath: makePath(callerComponent.valuePath, columnName),
                  modelConstructor: config.relationEntity as any,
                  parents,
                  disabled: relationBlueprint.type === 'view',
                },
              };
            } else {
              result.component = {
                is: config.isReadOnly ? 'CrudRelationLinks' : 'CrudMultiSelect',
                modelConstructor: config.relationEntity as any,
                properties: {
                  model: config.relationEntity as any,
                  modelConstructor: config.relationEntity as any,
                  endpoint: `/data-api/${relationBlueprint.endpoint}`,
                  searchFields: relationBlueprint.searchFields.default ?? [
                    'name',
                  ],
                  filters: filters,
                  valuePath: makePath(
                    callerComponent.valuePath,
                    config.propertyPath,
                  ),
                  trackBy: config.relationReferencedColumn || 'id',
                  label: 'id', //relationBlueprint.name?.nameProperties ??
                  customLabel: getEntityNameFormatter(config.relationEntity),
                  multiple,
                  nameProperties: relationBlueprint.name?.nameProperties,
                },
              };
            }
          }
        }
        if (
          result.component.is !== 'CrudRelationLinks' &&
          !result.component.embeddedForm
        ) {
          result.relationLinks = {
            is: 'CrudRelationQuickLink',
            modelConstructor: result.component.modelConstructor,
            properties: {
              ...result.component.properties,
              trackBy: 'id',
              variant: 'link',
            },
          };
        }
      }

      if (result.component === undefined) {
        const component = columnToComponent(
          config,
          callerComponent,
          isBulkEdit,
        );

        if (
          config.isReadOnly ||
          (callerComponent.entityId && config.isPrimary)
        ) {
          if (!component.properties) {
            component.properties = {};
          }
          component.properties.readonly = true;
          component.properties.disabled = true;
        }
        result.component = component;
      }

      result.component.properties.disabled =
        result.component?.properties?.disabled || callerComponent.disabled;

      // Component is configured, wrap it if needed
      if (result.component?.is) {
        if (
          !result.component.is.toString().startsWith('Crud') ||
          ['CrudMultiSelect', 'CrudPicklistMultiSelect'].includes(
            result.component.is.toString(),
          )
        ) {
          wrapComponent(result.component, 'ComponentWithPathWrapper');
        }
        if (
          result.component.is !== 'CrudEditArrayForm' &&
          result.component.is !== 'CrudFileInput' &&
          result.component.is !== 'CrudEnumPicker' &&
          result.component.is !== 'CrudPicklistMultiSelect' &&
          result.component.properties?.component !==
            'CrudPicklistMultiSelect' &&
          config.isArray
        ) {
          wrapComponent(result.component, 'CrudEditArrayForm');
        }
      }

      result.component.properties.emptyToNull = config.emptyToNull;

      return result as FieldsDescription;
    });
  return result;
}

export function _partnerExperienceLevel(totalJobs: number) {
  let variant = '';
  let text = '';
  const lowerJobsLimit = 6;
  const higherJobsLimit = 15;

  if (totalJobs <= lowerJobsLimit) {
    variant = 'danger';
    text = PartnerExperienceEnum.BEGINNER;
  }

  if (totalJobs > lowerJobsLimit && totalJobs <= higherJobsLimit) {
    variant = 'warning';
    text = PartnerExperienceEnum.INTERMEDIATE;
  }

  if (totalJobs > higherJobsLimit) {
    variant = 'success';
    text = PartnerExperienceEnum.EXPERIENCED;
  }

  return { variant, text };
}

export function getPartnerExperienceRange(experience: PartnerExperienceEnum) {
  switch (experience) {
    case PartnerExperienceEnum.BEGINNER:
      return [0, 6];
    case PartnerExperienceEnum.INTERMEDIATE:
      return [7, 15];
    case PartnerExperienceEnum.EXPERIENCED:
      return [16, Infinity];
    default:
      throw new Error('Invalid experience level');
  }
}

export const handleFetchError = (error: any) => {
  let message = '';
  if (axios.isAxiosError(error)) {
    if (error.response) {
      message = error.response.data.message;
    } else if (error.request) {
      message = error.request;
    } else {
      message = error.message;
    }
  } else {
    message = error.message;
  }
  return message;
};

export function getEnv(name) {
  return (window as any)?.env?.[name] ?? process?.env?.[name];
}
